"use client";

import { useState } from "react";
import { useTranslations } from "next-intl";
import { Link, usePathname } from "@/i18n/navigation";
import { useAuthStore } from "@/lib/authStore";
import { useRouter } from "@/i18n/navigation";

export default function Header() {
  const t = useTranslations("nav");
  const [menuOpen, setMenuOpen] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);
  const pathname = usePathname();
  const { user, logout } = useAuthStore();
  const router = useRouter();

  const handleLogout = async () => {
    await logout();
    router.push("/");
  };

  const aboutLinks = [
    { href: "/about-astrology/history-of-astrology", label: t("aboutAstrology") + " — " + "History" },
    { href: "/about-astrology/how-astrology-works", label: "How Astrology Works" },
    { href: "/about-astrology/zodiac-signs", label: t("signs") },
    { href: "/about-astrology/planets", label: t("planets") },
    { href: "/about-astrology/houses", label: t("houses") },
    { href: "/about-astrology/aspects", label: t("aspects") },
  ];

  return (
    <header className="bg-indigo-900 text-white shadow-md">
      <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
        {/* Logo */}
        <Link href="/" className="text-xl font-bold tracking-tight hover:text-indigo-200 transition-colors">
          The Solar Return
        </Link>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-6 text-sm font-medium">
          {/* About dropdown */}
          <div className="relative">
            <button
              onClick={() => setAboutOpen(!aboutOpen)}
              className="hover:text-indigo-200 transition-colors flex items-center gap-1"
            >
              {t("aboutAstrology")}
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {aboutOpen && (
              <div className="absolute top-full left-0 mt-2 w-52 bg-white text-gray-800 rounded shadow-lg z-50 py-1">
                {aboutLinks.map((link) => (
                  <Link
                    key={link.href}
                    href={link.href}
                    className="block px-4 py-2 text-sm hover:bg-indigo-50"
                    onClick={() => setAboutOpen(false)}
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
            )}
          </div>

          <Link href="/charts-and-reports" className="hover:text-indigo-200 transition-colors">
            {t("reports")}
          </Link>

          {user ? (
            <>
              <Link href="/account/members" className="hover:text-indigo-200 transition-colors">
                {t("members")}
              </Link>
              <button onClick={handleLogout} className="hover:text-indigo-200 transition-colors">
                {t("logout")}
              </button>
            </>
          ) : (
            <>
              <Link href="/account/login" className="hover:text-indigo-200 transition-colors">
                {t("login")}
              </Link>
              <Link
                href="/account/register"
                className="bg-indigo-600 hover:bg-indigo-500 px-3 py-1.5 rounded transition-colors"
              >
                {t("register")}
              </Link>
            </>
          )}
        </nav>

        {/* Mobile menu toggle */}
        <button
          className="md:hidden p-2"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Toggle menu"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            {menuOpen ? (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            ) : (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            )}
          </svg>
        </button>
      </div>

      {/* Mobile menu */}
      {menuOpen && (
        <div className="md:hidden border-t border-indigo-800 px-4 py-3 flex flex-col gap-3 text-sm">
          {aboutLinks.map((link) => (
            <Link key={link.href} href={link.href} onClick={() => setMenuOpen(false)}>
              {link.label}
            </Link>
          ))}
          <Link href="/charts-and-reports" onClick={() => setMenuOpen(false)}>{t("reports")}</Link>
          {user ? (
            <>
              <Link href="/account/members" onClick={() => setMenuOpen(false)}>{t("members")}</Link>
              <button onClick={handleLogout} className="text-left">{t("logout")}</button>
            </>
          ) : (
            <>
              <Link href="/account/login" onClick={() => setMenuOpen(false)}>{t("login")}</Link>
              <Link href="/account/register" onClick={() => setMenuOpen(false)}>{t("register")}</Link>
            </>
          )}
        </div>
      )}
    </header>
  );
}
